import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import SECTIONS from "modules/patient/sections";
import { RAIL_OPEN, RAIL_CLOSE } from "modules/patient/sections/railEvents";
import { ANTH_V2_SELECT_PARAM } from "modules/patient/sections/anthropometric/events";
import { PARAMETER_BY_KEY } from "modules/patient/sections/anthropometric/data";

import MOBILE from "../mobileConfig";
import BottomSheet from "./BottomSheet";

/**
 * Adding a record without leaving Create Rx.
 *
 * On desktop, "+ Add complaint" here pushes you to the patient record with
 * `?section=chief-complaints`, you add it, and you come back. On a phone that
 * round trip is the whole interaction, so this mounts the section's OWN editor
 * — the identical component the patient record mounts — in a sheet on this
 * screen.
 *
 * ALL FOUR ARE MOUNTED AT ONCE, and that is the point rather than an oversight.
 * An editor is what listens for its own add event; one that is not mounted
 * cannot be asked to open. They cost nothing while idle: useSheetStack starts
 * with an empty stack and SheetStack renders null for one.
 *
 * IT IS THE UPDATE SHEET TOO. A row's pencil (goEditIn) fires the section's own
 * EDIT channel, which the very same mounted rail answers by pushing an update
 * sheet seeded from that record — so adding and editing arrive here by the same
 * route and differ only in what the sheet is called.
 *
 * Nothing here writes. The form inside owns its service call, its validation
 * and its refresh event, exactly as it does on the patient record — this is a
 * frame around it and no more.
 */

/** Picker key -> patient section key. Mirrors PATIENT_SECTION_FOR_PICKER. */
const SECTION_KEYS = ["chief-complaints", "diagnosis", "lab-investigations", "medicines"];

/** Add-only wording for the sections MOBILE.SHEET_TITLES does not cover. */
const ADD_ONLY = {
  "lab-investigations": "Add lab investigation",
};

/**
 * Anthropometric data's editor, mounted beside the four above for ONE job: the
 * Patient details card's Weight opens its Weight card (MOBILE.rxWeightSheet).
 * Its save is the section's own POST, with its own version check, and it fires
 * ANTH_V2_REFRESH, which ReviewPrescription re-reads the weight from. It emits
 * RAIL_OPEN / RAIL_CLOSE like the others, so this same sheet opens around it.
 */
const ANTHRO_KEY = "anthropometric";

/** "Add complaint" / "Update complaint" — whichever channel opened the sheet. */
const titleFor = (which) => {
  if (!which) return "Add to the record";
  /* The parameter's own name — "Weight" — as the patient record's sheet says. */
  if (which.key === ANTHRO_KEY) {
    const meta = PARAMETER_BY_KEY[which.param];
    return (meta && meta.label) || "Anthropometric data";
  }
  const cfg = MOBILE.SHEET_TITLES[which.key];
  if (cfg) return which.mode === "edit" ? cfg.edit : cfg.add;
  return ADD_ONLY[which.key] || "Add to the record";
};

const MobileRxAddSheet = ({ patient, appointmentId }) => {
  const [open, setOpen] = useState(false);
  /** `{ key, mode }` — which section was asked for, and by which of its two
      channels, so the sheet can say "Add complaint" or "Update complaint".
      The same shape and the same reason as MobilePatientScreen's sheetAction. */
  const [which, setWhich] = useState(null);

  /* Bumping this remounts every editor, which drops their open sheets and
     fires RAIL_CLOSE from useSheetStack's own cleanup — the same dismissal the
     patient screen uses, and for the same reason: closeAll() belongs to the
     editor that owns it and cannot be reached from out here. */
  const [editorKey, setEditorKey] = useState(0);
  const dismiss = useCallback(() => {
    setWhich(null);
    setEditorKey((k) => k + 1);
  }, []);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    const onClose = () => setOpen(false);
    EventService.addListener(RAIL_OPEN, onOpen);
    EventService.addListener(RAIL_CLOSE, onClose);

    /* Which one was asked for and how, so the sheet can say so. These are the
       same channels goAddIn and goEditIn emit — and, before either, the ones
       the section's own table and status chips already use. Listening is
       additive: nothing new is sent. */
    const offs = SECTION_KEYS.map((key) => {
      const cfg = MOBILE.SHEET_TITLES[key];
      const channels = cfg
        ? [[cfg.addEvent, "add"], [cfg.editEvent, "edit"]]
        : [[MOBILE.ADD_EVENT_FOR_TAB[key], "add"]];
      const marks = channels
        .filter(([channel]) => !!channel)
        .map(([channel, mode]) => {
          const mark = () => setWhich({ key, mode });
          EventService.addListener(channel, mark);
          return () => EventService.removeListener(channel, mark);
        });
      return () => marks.forEach((off) => off());
    });

    const onParam = (param) => setWhich({ key: ANTHRO_KEY, param });
    if (MOBILE.rxWeightSheet) EventService.addListener(ANTH_V2_SELECT_PARAM, onParam);

    return () => {
      EventService.removeListener(RAIL_OPEN, onOpen);
      EventService.removeListener(RAIL_CLOSE, onClose);
      EventService.removeListener(ANTH_V2_SELECT_PARAM, onParam);
      offs.forEach((off) => off());
    };
  }, []);

  if (!patient) return null;

  const AnthroEditor =
    MOBILE.rxWeightSheet && SECTIONS[ANTHRO_KEY] ? SECTIONS[ANTHRO_KEY].Editor : null;

  return (
    <BottomSheet
      open={open}
      onClose={dismiss}
      title={titleFor(which)}
      className="zym-sheet-editor zym-sheet-rx"
      clearTopBar
    >
      {SECTION_KEYS.map((key) => {
        const entry = SECTIONS[key];
        if (!entry || !entry.Editor) return null;
        const Editor = entry.Editor;
        return (
          <Editor
            key={`${key}-${editorKey}`}
            patient={patient}
            appointmentId={appointmentId}
          />
        );
      })}
      {AnthroEditor && <AnthroEditor key={`${ANTHRO_KEY}-${editorKey}`} patient={patient} />}
    </BottomSheet>
  );
};

MobileRxAddSheet.propTypes = {
  patient: PropTypes.object,
  appointmentId: PropTypes.number,
};

export default MobileRxAddSheet;
